import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import './FlightRequestPage.css';

const FlightRequestPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    destination: '',
    date: '',
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const dest = searchParams.get('destination');
    const date = searchParams.get('date');
    if (dest || date) {
      setFormData(prev => ({
        ...prev,
        destination: dest || prev.destination,
        date: date || prev.date
      }));
    }
  }, [searchParams]);

  const today = new Date().toISOString().slice(0, 10);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!formData.destination?.trim()) {
      setError(t('flightRequest.errDestination'));
      return;
    }
    if (!formData.date) {
      setError(t('flightRequest.errDate'));
      return;
    }
    if (formData.date < today) {
      setError(t('flightRequest.errDateFuture'));
      return;
    }
    if (!formData.name?.trim()) {
      setError(t('flightRequest.errName'));
      return;
    }
    if (!formData.email?.trim()) {
      setError(t('flightRequest.errEmail'));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/api/flight-requests', {
        destination: formData.destination.trim(),
        date: formData.date,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone?.trim() || null,
        notes: formData.notes?.trim() || null
      });
      setSuccess(true);
      setFormData({ destination: '', date: '', name: '', email: '', phone: '', notes: '' });
    } catch (err) {
      setError(err.response?.data?.error || err.message || t('flightRequest.errGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="flight-request-page container">
        <div className="card flight-request-success-card">
          <div className="flight-request-success-icon">✓</div>
          <h1>{t('flightRequest.successTitle')}</h1>
          <p>{t('flightRequest.success')}</p>
          <Link to="/booking/companions" className="btn btn-primary">{t('flightRequest.viewCompanions')}</Link>
          <Link to="/booking" className="btn btn-secondary">{t('flightRequest.backToBooking')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flight-request-page container">
      <h1>{t('flightRequest.title')}</h1>
      <p className="flight-request-intro">{t('flightRequest.intro')}</p>

      {error && <div className="error" role="alert">{error}</div>}

      <div className="card flight-request-form-card">
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label>{t('flightRequest.destination')} {t('common.required')}</label>
            <input
              type="text"
              value={formData.destination}
              onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
              placeholder="e.g. Vienna, Bratislava, Lake Balaton"
              required
            />
          </div>
          <div className="form-group">
            <label>{t('flightRequest.date')} {t('common.required')}</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              min={today}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('flightRequest.name')} {t('common.required')}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('flightRequest.email')} {t('common.required')}</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('flightRequest.phone')}</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>{t('flightRequest.notes')}</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
              placeholder="e.g. flexible on time, 2 passengers"
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-success" disabled={submitting}>
              {submitting ? t('flightRequest.submitting') : t('flightRequest.submit')}
            </button>
            <Link to="/booking" className="btn btn-secondary">{t('flightRequest.backToBooking')}</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FlightRequestPage;
